import React from 'react';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Construction,
  Droplets,
  Hammer,
  HeartHandshake,
  Lightbulb,
  Mountain,
  Phone,
  Ruler,
  ShieldCheck,
  Sparkles,
  Wrench,
  Zap
} from 'lucide-react';
import Seo from '../components/Seo';
import { FadeIn } from '../components/ScrollReveal';

const serviceCategories = [
  {
    id: 'planung',
    number: '01',
    title: 'Beratung & Planung',
    icon: Lightbulb,
    intro: 'Eine gute Fläche beginnt mit einer klaren Planung – technisch, gestalterisch und finanziell.',
    services: [
      'Persönliche Beratung vor Ort',
      'Aufmaß, Materialbedarf und Zeitplanung',
      'Fliesen-, Format- und Fugenberatung',
      'Transparenter Kostenvoranschlag'
    ]
  },
  {
    id: 'fliesenarbeiten',
    number: '02',
    title: 'Fliesenarbeiten',
    icon: Ruler,
    intro: 'Präzise Verlegung für ruhige Fugenbilder, saubere Kanten und belastbare Oberflächen.',
    services: [
      'Keramik und Feinsteinzeug',
      'Großformat- und XXL-Fliesen',
      'Mosaik und individuelle Akzente',
      'Wand-, Boden- und Treppenflächen',
      'Naturstein im Innen- und Außenbereich'
    ]
  },
  {
    id: 'sanierung',
    number: '03',
    title: 'Sanierung & Renovierung',
    icon: Construction,
    intro: 'Bestehende Räume werden technisch sauber erneuert und sinnvoll für den Alltag geplant.',
    services: [
      'Komplette Badsanierung',
      'Küchenmodernisierung',
      'Altbausanierung',
      'Balkon- und Terrassensanierung',
      'Rückbau und Altfliesenentfernung'
    ]
  },
  {
    id: 'spezialarbeiten',
    number: '04',
    title: 'Spezialarbeiten',
    icon: ShieldCheck,
    intro: 'Für Nassbereiche und anspruchsvolle Details braucht es Erfahrung mit Aufbau, Abdichtung und Anschlüssen.',
    services: [
      'Abdichtung nach geltenden Normen',
      'Bodengleiche und barrierearme Duschen',
      'Trockenbau und Vorwandflächen',
      'Spachtel-, Putz- und Silikonarbeiten',
      'Fußbodenheizung mit Fachpartnern'
    ]
  },
  {
    id: 'reparaturen',
    number: '05',
    title: 'Service & Reparaturen',
    icon: Wrench,
    intro: 'Nicht jede Fläche muss komplett neu. Schäden und Verschleiß werden gezielt und fachgerecht behoben.',
    services: [
      'Austausch beschädigter Fliesen',
      'Fugen- und Silikonerneuerung',
      'Ausbesserungsarbeiten',
      'Schadensanalyse bei Feuchtigkeit',
      'Fachgerechte Entsorgung'
    ]
  }
];

const specialties = [
  { icon: HeartHandshake, title: 'Barrierearme Bäder', text: 'Sicher, komfortabel und vorausschauend geplant.' },
  { icon: Mountain, title: 'Naturstein', text: 'Materialgerecht verarbeitet und langlebig geschützt.' },
  { icon: Droplets, title: 'Nassraum-Abdichtung', text: 'Saubere Systemaufbauten für dauerhaften Schutz.' },
  { icon: Zap, title: 'Fußbodenheizung', text: 'Koordiniert mit zuverlässigen Elektro-Fachpartnern.' }
];

const workflow = [
  { icon: Phone, title: 'Kennenlernen', text: 'Sie schildern Ihr Vorhaben. Wir klären Umfang, Wünsche und den passenden nächsten Schritt.' },
  { icon: Ruler, title: 'Aufmaß & Angebot', text: 'Vor Ort werden Flächen geprüft, Maße genommen und Leistungen transparent kalkuliert.' },
  { icon: Hammer, title: 'Saubere Ausführung', text: 'Der vereinbarte Aufbau wird präzise, ordentlich und mit laufender Abstimmung umgesetzt.' },
  { icon: CheckCircle2, title: 'Abnahme', text: 'Wir prüfen gemeinsam das Ergebnis und übergeben die Fläche sauber und einsatzbereit.' }
];

const ServicesPage = () => {
  const siteUrl = 'https://fliesen-jarek.de';
  const servicesSchema = {
    '@context': 'https://schema.org',
    '@graph': serviceCategories.flatMap((category) => category.services.map((service) => ({
      '@type': 'Service',
      name: service,
      serviceType: service,
      description: category.intro,
      provider: { '@id': `${siteUrl}/#business` },
      areaServed: { '@type': 'City', name: 'Cuxhaven' },
      url: `${siteUrl}/leistungen#${category.id}`
    })))
  };

  return (
    <>
      <Seo
        title="Leistungen – Fliesenarbeiten und Badsanierung in Cuxhaven"
        description="Fliesenverlegung, Badsanierung, Naturstein, Abdichtung und Reparaturen aus einer Hand. Persönlich geplant und sauber ausgeführt in Cuxhaven und Umgebung."
        schema={servicesSchema}
      />

      <section className="relative overflow-hidden bg-stone-950 text-white">
        <div className="absolute -right-20 top-0 h-96 w-96 rounded-full bg-primary-600/20 blur-3xl" aria-hidden="true" />
        <div className="absolute -left-32 bottom-0 h-72 w-72 rounded-full bg-primary-900/30 blur-3xl" aria-hidden="true" />
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-24 lg:grid-cols-[1.25fr_0.75fr] lg:items-center lg:py-28">
          <FadeIn>
            <div className="relative">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-primary-200">
                <Sparkles className="h-4 w-4" />
                Fliesenarbeiten in Cuxhaven
              </div>
              <h1 className="max-w-4xl text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
                Räume, die gut aussehen.
                <span className="block text-primary-300">Arbeit, die lange hält.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-stone-300 sm:text-xl">
                Von der einzelnen Reparatur bis zur kompletten Badsanierung: persönlich geplant,
                technisch sauber aufgebaut und präzise ausgeführt.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a href="/#contact" className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-7 py-4 font-semibold text-white transition hover:bg-primary-500">
                  Projekt besprechen <ArrowRight className="h-5 w-5" />
                </a>
                <a href="/#gallery" className="inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/5 px-7 py-4 font-semibold text-white transition hover:bg-white/10">
                  Referenzen ansehen
                </a>
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={150}>
            <aside className="relative rounded-3xl border border-white/10 bg-white/[0.07] p-6 backdrop-blur sm:p-8">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-300">Das können Sie erwarten</p>
              <ul className="mt-6 space-y-5">
                {['Ein fester Ansprechpartner', 'Klare Leistungen und transparente Kosten', 'Saubere Baustelle und verlässliche Termine', '5 Jahre Gewährleistung auf die Arbeiten'].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-stone-200">
                    <span className="mt-0.5 rounded-full bg-primary-500/20 p-1"><Check className="h-4 w-4 text-primary-300" /></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </aside>
          </FadeIn>
        </div>
      </section>

      <nav aria-label="Leistungsübersicht" className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-4 py-4 sm:px-6">
          {serviceCategories.map((category) => (
            <a key={category.id} href={`#${category.id}`} className="whitespace-nowrap rounded-full bg-stone-100 px-4 py-2 text-sm font-medium text-stone-700 transition hover:bg-primary-50 hover:text-primary-700">
              {category.title}
            </a>
          ))}
        </div>
      </nav>

      <section className="bg-stone-50 py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <FadeIn>
            <div className="mb-12 max-w-3xl sm:mb-16">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-700">Leistungsspektrum</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-stone-900 sm:text-4xl">Alles, was eine gute Fläche braucht.</h2>
              <p className="mt-4 text-lg leading-8 text-stone-600">Übersichtlich gegliedert, damit Sie schnell sehen, wobei wir Sie unterstützen können.</p>
            </div>
          </FadeIn>

          <div className="grid gap-6 lg:grid-cols-2">
            {serviceCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <FadeIn key={category.id} delay={(index % 2) * 100}>
                  <article id={category.id} className={`scroll-mt-28 h-full rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8 ${index === serviceCategories.length - 1 ? 'lg:col-span-2' : ''}`}>
                    <div className="flex items-start justify-between gap-6">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                        <Icon className="h-6 w-6" />
                      </div>
                      <span className="text-sm font-semibold tracking-[0.2em] text-stone-300">{category.number}</span>
                    </div>
                    <h3 className="mt-6 text-2xl font-semibold tracking-tight text-stone-900">{category.title}</h3>
                    <p className="mt-3 max-w-2xl leading-7 text-stone-600">{category.intro}</p>
                    <ul className={`mt-7 grid gap-3 ${index === serviceCategories.length - 1 ? 'sm:grid-cols-2' : ''}`}>
                      {category.services.map((service) => (
                        <li key={service} className="flex items-start gap-3 rounded-xl bg-stone-50 px-4 py-3 text-sm font-medium text-stone-700">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-primary-600" />
                          {service}
                        </li>
                      ))}
                    </ul>
                  </article>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <FadeIn>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-700">Besondere Kompetenz</p>
                <h2 className="mt-3 text-3xl font-semibold tracking-tight text-stone-900 sm:text-4xl">Details, bei denen Erfahrung zählt.</h2>
                <p className="mt-4 text-lg leading-8 text-stone-600">Gerade bei Feuchtigkeit, Naturstein und barrierearmen Lösungen entscheidet der richtige Aufbau über die Lebensdauer.</p>
              </div>
            </FadeIn>
            <div className="grid gap-4 sm:grid-cols-2">
              {specialties.map(({ icon: Icon, title, text }, index) => (
                <FadeIn key={title} delay={index * 75}>
                  <div className="h-full rounded-2xl border border-stone-200 p-6 transition hover:border-primary-200 hover:shadow-lg">
                    <Icon className="h-7 w-7 text-primary-600" />
                    <h3 className="mt-5 text-lg font-semibold text-stone-900">{title}</h3>
                    <p className="mt-2 leading-7 text-stone-600">{text}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-stone-950 py-20 text-white sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <FadeIn>
            <div className="mx-auto mb-14 max-w-3xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-300">So läuft Ihr Projekt</p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">Vier Schritte. Klare Zuständigkeit.</h2>
            </div>
          </FadeIn>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {workflow.map(({ icon: Icon, title, text }, index) => (
              <FadeIn key={title} delay={index * 100}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6">
                  <div className="flex items-center justify-between">
                    <Icon className="h-6 w-6 text-primary-300" />
                    <span className="text-sm font-semibold text-stone-500">0{index + 1}</span>
                  </div>
                  <h3 className="mt-8 text-xl font-semibold">{title}</h3>
                  <p className="mt-3 leading-7 text-stone-400">{text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary-600 py-16 text-white sm:py-20">
        <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Was möchten Sie verändern?</h2>
            <p className="mt-3 text-lg text-primary-50">Ein paar Fotos und eine kurze Beschreibung reichen für den ersten Austausch.</p>
          </div>
          <a href="/#contact" className="inline-flex flex-none items-center justify-center gap-2 rounded-xl bg-white px-7 py-4 font-semibold text-stone-900 transition hover:bg-primary-50">
            Unverbindlich anfragen <ArrowRight className="h-5 w-5" />
          </a>
        </div>
      </section>
    </>
  );
};

export default ServicesPage;
